import {isAxiosError} from 'axios';
import Sentry from '@/services/sentry/Sentry';
import axios from '@/utils/axios';

export type UploadedMessageAttachment = {
  sourceId: string;
  fileName: string;
  mimeType: string;
};

type PresignedUploadResponse = {
  url: string;
  key: string;
  content_type?: string;
};

const UNSUPPORTED_TYPE_MESSAGE = 'This file type is not supported';

export function messageAttachmentUploadErrorDetail(
  error: unknown
): string | undefined {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return undefined;
  }
  const apiError = (error.response.data as {error?: string} | undefined)?.error;
  if (typeof apiError === 'string' && apiError.trim()) {
    return apiError;
  }
  return UNSUPPORTED_TYPE_MESSAGE;
}

function reportUnexpectedUploadFailure(error: unknown, file: File): void {
  if (isAxiosError(error) && error.response?.status === 422) {
    return;
  }
  const exception = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(exception, {
    message: 'Message attachment upload failed',
    fileName: file.name,
    mimeType: file.type,
  });
}

export async function uploadMessageAttachment(
  file: File
): Promise<UploadedMessageAttachment> {
  try {
    const extension = file.name.includes('.')
      ? file.name.split('.').pop()!.toLowerCase()
      : '';

    const {data} = await axios.get<PresignedUploadResponse>(
      '/uploads/presigned-url',
      {
        params: {
          bucket: 'message_attachments',
          extension,
          file_name: file.name,
        },
      }
    );

    const contentType = data.content_type || file.type;
    const putResponse = await fetch(data.url, {
      method: 'PUT',
      headers: {'Content-Type': contentType},
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error(`Upload failed (${putResponse.status})`);
    }

    const key = data.key.startsWith('/') ? data.key : `/${data.key}`;

    return {
      sourceId: key,
      fileName: file.name,
      mimeType: file.type || contentType,
    };
  } catch (error) {
    reportUnexpectedUploadFailure(error, file);
    throw error;
  }
}
